import { useCallback, useMemo } from "react";
import { ADMIN_LIST_PAGE_SIZE } from "../dashboard/Config";

export default function Pagination({
  adminListPages,
  setAdminListPages,
  texts,
  positionRows,
  filteredAdminUsers,
  filteredAdminProducts,
  filteredAdminOrders,
  bannerDrafts,
  collectedProducts
}) {
  const paginateAdminList = useCallback((items, pageKey) => {
    const list = Array.isArray(items) ? items : [];
    const totalItems = list.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / ADMIN_LIST_PAGE_SIZE));
    const requestedPage = Number(adminListPages[pageKey] || 1);
    const currentPage = Math.min(Math.max(requestedPage, 1), totalPages);
    const startIndex = (currentPage - 1) * ADMIN_LIST_PAGE_SIZE;

    return {
      items: list.slice(startIndex, startIndex + ADMIN_LIST_PAGE_SIZE),
      currentPage,
      totalPages,
      totalItems,
      startIndex
    };
  }, [adminListPages]);

  const paginatedPositionRows = useMemo(() => paginateAdminList(positionRows, "positions"), [paginateAdminList, positionRows]);
  const paginatedUserManagementRows = useMemo(
    () => paginateAdminList(filteredAdminUsers, "userManagement"),
    [filteredAdminUsers, paginateAdminList]
  );
  const paginatedProductRows = useMemo(
    () => paginateAdminList(filteredAdminProducts, "products"),
    [filteredAdminProducts, paginateAdminList]
  );
  const paginatedOrderRows = useMemo(
    () => paginateAdminList(filteredAdminOrders, "orders"),
    [filteredAdminOrders, paginateAdminList]
  );
  const paginatedCustomerRows = useMemo(
    () => paginateAdminList(filteredAdminUsers, "customers"),
    [filteredAdminUsers, paginateAdminList]
  );
  const paginatedBannerRows = useMemo(() => paginateAdminList(bannerDrafts, "banners"), [bannerDrafts, paginateAdminList]);
  const paginatedCollectedProductRows = useMemo(
    () => paginateAdminList(collectedProducts, "collectedProducts"),
    [collectedProducts, paginateAdminList]
  );

  const renderPagination = useCallback((pageKey, meta) => {
    if (!meta || meta.totalItems <= 0) {
      return null;
    }

    const from = meta.startIndex + 1;
    const to = Math.min(meta.startIndex + ADMIN_LIST_PAGE_SIZE, meta.totalItems);

    return (
      <div className="controlhub-pagination" role="navigation" aria-label={`${pageKey}-pagination`}>
        <span className="controlhub-pagination-summary">
          {texts.paginationTotal} {meta.totalItems} | {from}-{to}
        </span>
        <div className="controlhub-pagination-actions">
          <button
            type="button"
            className="controlhub-button ghost"
            onClick={() => setAdminListPages((prev) => ({ ...prev, [pageKey]: Math.max(1, meta.currentPage - 1) }))}
            disabled={meta.currentPage <= 1}
          >
            {texts.paginationPrevious}
          </button>
          <span className="controlhub-pagination-page">
            {texts.paginationPage} {meta.currentPage} / {meta.totalPages}
          </span>
          <button
            type="button"
            className="controlhub-button ghost"
            onClick={() => setAdminListPages((prev) => ({ ...prev, [pageKey]: Math.min(meta.totalPages, meta.currentPage + 1) }))}
            disabled={meta.currentPage >= meta.totalPages}
          >
            {texts.paginationNext}
          </button>
        </div>
      </div>
    );
  }, [setAdminListPages, texts]);

  return {
    paginatedPositionRows,
    paginatedUserManagementRows,
    paginatedProductRows,
    paginatedOrderRows,
    paginatedCustomerRows,
    paginatedBannerRows,
    paginatedCollectedProductRows,
    renderPagination
  };
}
